import React, { useState, useEffect } from 'react';
import { 
  Activity, 
  AlertTriangle, 
  CheckCircle, 
  Clock, 
  FileText, 
  TrendingUp, 
  Users, 
  Zap,
  Search,
  Filter,
  Eye,
  Download,
  Share2,
  MoreHorizontal
} from 'lucide-react';
import { theme } from '../../styles/theme';

interface LabResult {
  id: string;
  patientName: string;
  testType: string;
  status: 'pending' | 'completed' | 'abnormal' | 'critical';
  priority: 'low' | 'medium' | 'high' | 'urgent';
  timestamp: string;
  value?: string;
  reference?: string;
  notes?: string;
}

interface AIInsight {
  id: string;
  type: 'pattern' | 'anomaly' | 'trend' | 'correlation';
  severity: 'low' | 'medium' | 'high' | 'critical';
  title: string;
  description: string;
  confidence: number;
  timestamp: string;
  affectedTests: string[];
}

type TabType = 'pending' | 'insights' | 'education' | 'patterns';

export const LabTechDashboard: React.FC = () => {
  const [activeTab, setActiveTab] = useState<TabType>('pending');
  const [pendingResults, setPendingResults] = useState<LabResult[]>([]);
  const [aiInsights, setAiInsights] = useState<AIInsight[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // Mock data
  useEffect(() => {
    const mockPendingResults: LabResult[] = [
      {
        id: '1',
        patientName: 'Sarah Johnson',
        testType: 'Complete Blood Count',
        status: 'pending',
        priority: 'high',
        timestamp: '2024-01-15T10:30:00Z',
        notes: 'Patient has history of anemia'
      },
      {
        id: '2',
        patientName: 'Michael Chen',
        testType: 'Lipid Panel',
        status: 'pending',
        priority: 'medium',
        timestamp: '2024-01-15T09:15:00Z'
      },
      {
        id: '3',
        patientName: 'Emily Rodriguez',
        testType: 'Thyroid Function',
        status: 'pending',
        priority: 'urgent',
        timestamp: '2024-01-15T08:45:00Z',
        notes: 'Critical values detected'
      }
    ];

    const mockAIInsights: AIInsight[] = [
      {
        id: '1',
        type: 'anomaly',
        severity: 'high',
        title: 'Elevated CRP Pattern',
        description: 'Multiple patients showing elevated C-reactive protein levels, suggesting potential inflammatory response pattern.',
        confidence: 87,
        timestamp: '2024-01-15T10:00:00Z',
        affectedTests: ['CRP', 'ESR', 'WBC']
      },
      {
        id: '2',
        type: 'trend',
        severity: 'medium',
        title: 'Seasonal Vitamin D Deficiency',
        description: 'Increased prevalence of vitamin D deficiency during winter months.',
        confidence: 92,
        timestamp: '2024-01-15T09:30:00Z',
        affectedTests: ['Vitamin D', 'Calcium', 'PTH']
      }
    ];

    setTimeout(() => {
      setPendingResults(mockPendingResults);
      setAiInsights(mockAIInsights);
      setIsLoading(false);
    }, 1000);
  }, []);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return 'text-green-600 bg-green-50 dark:bg-green-900/20 dark:text-green-400';
      case 'pending': return 'text-yellow-600 bg-yellow-50 dark:bg-yellow-900/20 dark:text-yellow-400';
      case 'abnormal': return 'text-orange-600 bg-orange-50 dark:bg-orange-900/20 dark:text-orange-400';
      case 'critical': return 'text-red-600 bg-red-50 dark:bg-red-900/20 dark:text-red-400';
      default: return 'text-slate-600 bg-slate-50 dark:bg-slate-700 dark:text-slate-400';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'urgent': return 'text-red-600 bg-red-50 dark:bg-red-900/20 dark:text-red-400';
      case 'high': return 'text-orange-600 bg-orange-50 dark:bg-orange-900/20 dark:text-orange-400';
      case 'medium': return 'text-yellow-600 bg-yellow-50 dark:bg-yellow-900/20 dark:text-yellow-400';
      case 'low': return 'text-green-600 bg-green-50 dark:bg-green-900/20 dark:text-green-400';
      default: return 'text-slate-600 bg-slate-50 dark:bg-slate-700 dark:text-slate-400';
    }
  };

  const getInsightIcon = (type: string) => {
    switch (type) {
      case 'anomaly': return AlertTriangle;
      case 'pattern': return TrendingUp;
      case 'trend': return Activity;
      case 'correlation': return Zap;
      default: return Activity;
    }
  };

  const getInsightColor = (severity: string) => {
    switch (severity) {
      case 'critical': return 'text-red-600 bg-red-50 dark:bg-red-900/20 dark:text-red-400';
      case 'high': return 'text-orange-600 bg-orange-50 dark:bg-orange-900/20 dark:text-orange-400';
      case 'medium': return 'text-yellow-600 bg-yellow-50 dark:bg-yellow-900/20 dark:text-yellow-400';
      case 'low': return 'text-green-600 bg-green-50 dark:bg-green-900/20 dark:text-green-400';
      default: return 'text-slate-600 bg-slate-50 dark:bg-slate-700 dark:text-slate-400';
    }
  };

  const totalAlerts = aiInsights.filter(insight => insight.severity === 'high' || insight.severity === 'critical').length;

  if (isLoading) {
    return (
      <div className={`${theme.colors.background.card} rounded-xl p-6`}>
        <div className="animate-pulse">
          <div className="h-4 bg-slate-200 dark:bg-slate-700 rounded w-1/3 mb-4"></div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[...Array(8)].map((_, i) => (
              <div key={i} className="h-16 bg-slate-200 dark:bg-slate-700 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className={`${theme.colors.background.card} rounded-xl p-6 border ${theme.colors.border.primary}`}>
          <div className="flex items-center justify-between">
            <div>
              <p className={`${theme.colors.text.tertiary} text-sm`}>Pending Results</p>
              <p className={`${theme.colors.text.primary} text-2xl font-bold`}>{pendingResults.length}</p>
            </div>
            <div className="p-3 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
              <Clock className="h-6 w-6 text-blue-600 dark:text-blue-400" />
            </div>
          </div>
        </div>

        <div className={`${theme.colors.background.card} rounded-xl p-6 border ${theme.colors.border.primary}`}>
          <div className="flex items-center justify-between">
            <div>
              <p className={`${theme.colors.text.tertiary} text-sm`}>AI Insights</p>
              <p className={`${theme.colors.text.primary} text-2xl font-bold`}>{aiInsights.length}</p>
            </div>
            <div className="p-3 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
              <Zap className="h-6 w-6 text-purple-600 dark:text-purple-400" />
            </div>
          </div>
        </div>

        <div className={`${theme.colors.background.card} rounded-xl p-6 border ${theme.colors.border.primary}`}>
          <div className="flex items-center justify-between">
            <div>
              <p className={`${theme.colors.text.tertiary} text-sm`}>Critical Alerts</p>
              <p className={`${theme.colors.text.primary} text-2xl font-bold`}>{totalAlerts}</p>
            </div>
            <div className="p-3 bg-red-100 dark:bg-red-900/30 rounded-lg">
              <AlertTriangle className="h-6 w-6 text-red-600 dark:text-red-400" />
            </div>
          </div>
        </div>

        <div className={`${theme.colors.background.card} rounded-xl p-6 border ${theme.colors.border.primary}`}>
          <div className="flex items-center justify-between">
            <div>
              <p className={`${theme.colors.text.tertiary} text-sm`}>Completed Today</p>
              <p className={`${theme.colors.text.primary} text-2xl font-bold`}>24</p>
            </div>
            <div className="p-3 bg-green-100 dark:bg-green-900/30 rounded-lg">
              <CheckCircle className="h-6 w-6 text-green-600 dark:text-green-400" />
            </div>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className={`${theme.colors.background.card} rounded-xl border ${theme.colors.border.primary} p-6`}>
        <div className="flex space-x-4">
          {[
            { id: 'pending', label: 'Pending Results', count: pendingResults.length },
            { id: 'insights', label: 'AI Insights', count: totalAlerts },
            { id: 'education', label: 'Clinical Education' },
            { id: 'patterns', label: 'Pattern Analysis' }
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as TabType)}
              className={`flex items-center space-x-2 px-4 py-3 rounded-lg transition-colors ${
                activeTab === tab.id
                  ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-blue-800'
                  : `${theme.colors.text.tertiary} ${theme.colors.interactive.hover}`
              }`}
            >
              <span className="font-medium">{tab.label}</span>
              {tab.count && (
                <span className={`px-2 py-1 text-xs rounded-full ${
                  activeTab === tab.id ? 'bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300' : 'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-400'
                }`}>
                  {tab.count}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Tab Content */}
      <div className={`${theme.colors.background.card} rounded-xl border ${theme.colors.border.primary} p-6`}>
        {activeTab === 'pending' && (
          <div className="space-y-4">
            <div className="flex items-center justify-between mb-6">
              <h3 className={`text-lg font-semibold ${theme.colors.text.primary}`}>Pending Lab Results</h3>
              <div className="flex items-center space-x-3">
                <div className="relative">
                  <Search className="absolute left-3 top-3 h-4 w-4 text-slate-400 dark:text-slate-500" />
                  <input
                    type="text"
                    placeholder="Search results..."
                    className={`pl-10 pr-4 py-2 border ${theme.colors.border.secondary} rounded-lg ${theme.colors.text.primary} ${theme.colors.background.primary} focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-transparent`}
                  />
                </div>
                <button className={`flex items-center space-x-2 px-3 py-2 border ${theme.colors.border.secondary} rounded-lg ${theme.colors.interactive.hover}`}>
                  <Filter className="h-4 w-4" />
                  <span>Filter</span>
                </button>
              </div>
            </div>

            <div className="space-y-3">
              {pendingResults.map((result) => (
                <div key={result.id} className={`p-4 border ${theme.colors.border.primary} rounded-lg ${theme.colors.interactive.hover} transition-colors`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                      <div className="p-2 bg-slate-100 dark:bg-slate-700 rounded-lg">
                        <FileText className="h-5 w-5 text-slate-600 dark:text-slate-400" />
                      </div>
                      <div>
                        <h4 className={`font-medium ${theme.colors.text.primary}`}>{result.patientName}</h4>
                        <p className={`text-sm ${theme.colors.text.tertiary}`}>{result.testType}</p>
                        {result.notes && (
                          <p className={`text-xs ${theme.colors.text.muted} mt-1`}>{result.notes}</p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center space-x-3">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(result.status)}`}>
                        {result.status}
                      </span>
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${getPriorityColor(result.priority)}`}>
                        {result.priority}
                      </span>
                      <div className="flex items-center space-x-2">
                        <button className="p-1 hover:bg-slate-100 dark:hover:bg-slate-700 rounded">
                          <Eye className="h-4 w-4 text-slate-600 dark:text-slate-400" />
                        </button>
                        <button className="p-1 hover:bg-slate-100 dark:hover:bg-slate-700 rounded">
                          <Download className="h-4 w-4 text-slate-600 dark:text-slate-400" />
                        </button>
                        <button className="p-1 hover:bg-slate-100 dark:hover:bg-slate-700 rounded">
                          <MoreHorizontal className="h-4 w-4 text-slate-600 dark:text-slate-400" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {activeTab === 'insights' && (
          <div className="space-y-4">
            <div className="flex items-center justify-between mb-6">
              <h3 className={`text-lg font-semibold ${theme.colors.text.primary}`}>AI-Generated Insights</h3>
              <button className={`flex items-center space-x-2 px-3 py-2 border ${theme.colors.border.secondary} rounded-lg ${theme.colors.interactive.hover}`}>
                <Share2 className="h-4 w-4" />
                <span>Export Report</span>
              </button>
            </div>

            <div className="space-y-4">
              {aiInsights.map((insight) => {
                const InsightIcon = getInsightIcon(insight.type);
                return (
                  <div key={insight.id} className={`p-4 border ${theme.colors.border.primary} rounded-lg ${theme.colors.interactive.hover} transition-colors`}>
                    <div className="flex items-start justify-between">
                      <div className="flex items-start space-x-4">
                        <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
                          <InsightIcon className="h-5 w-5 text-purple-600 dark:text-purple-400" />
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center space-x-3 mb-2">
                            <h4 className={`font-medium ${theme.colors.text.primary}`}>{insight.title}</h4>
                            <span className={`px-2 py-1 text-xs font-medium rounded-full ${getInsightColor(insight.severity)}`}>
                              {insight.severity}
                            </span>
                            <span className="text-xs text-slate-500 dark:text-slate-400">
                              {insight.confidence}% confidence
                            </span>
                          </div>
                          <p className={`text-sm ${theme.colors.text.secondary} mb-3`}>{insight.description}</p>
                          <div className="flex items-center space-x-4 text-xs text-slate-500 dark:text-slate-400">
                            <span>Affected: {insight.affectedTests.join(', ')}</span>
                            <span>•</span>
                            <span>{new Date(insight.timestamp).toLocaleString()}</span>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        <button className="p-1 hover:bg-slate-100 dark:hover:bg-slate-700 rounded">
                          <Eye className="h-4 w-4 text-slate-600 dark:text-slate-400" />
                        </button>
                        <button className="p-1 hover:bg-slate-100 dark:hover:bg-slate-700 rounded">
                          <MoreHorizontal className="h-4 w-4 text-slate-600 dark:text-slate-400" />
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {activeTab === 'education' && (
          <div className="text-center py-12">
            <div className="p-4 bg-blue-100 dark:bg-blue-900/30 rounded-full w-16 h-16 mx-auto mb-4">
              <Users className="h-8 w-8 text-blue-600 dark:text-blue-400" />
            </div>
            <h3 className={`text-lg font-semibold ${theme.colors.text.primary} mb-2`}>Clinical Education</h3>
            <p className={`${theme.colors.text.tertiary} max-w-md mx-auto`}>
              Access continuing education modules, case studies, and clinical guidelines to enhance your expertise.
            </p>
          </div>
        )}

        {activeTab === 'patterns' && (
          <div className="text-center py-12">
            <div className="p-4 bg-purple-100 dark:bg-purple-900/30 rounded-full w-16 h-16 mx-auto mb-4">
              <TrendingUp className="h-8 w-8 text-purple-600 dark:text-purple-400" />
            </div>
            <h3 className={`text-lg font-semibold ${theme.colors.text.primary} mb-2`}>Pattern Analysis</h3>
            <p className={`${theme.colors.text.tertiary} max-w-md mx-auto`}>
              Advanced analytics to identify trends, correlations, and patterns across patient populations.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};
